/**
 * AutoCount - Inventory Scan Page
 * Manual barcode entry path for the scanner workflow: enter a part number /
 * OEM number, pick a scan mode, and submit. Count modes record an observed
 * quantity (reconciled later); receive/pick/adjust modes move stock.
 */

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  submitScan,
  getPartByBarcode,
  ScanMode,
  ScanResult,
  Part,
  ApiError,
} from "api";

interface ScanModeOption {
  label: string;
  value: ScanMode;
  usesQuantity: boolean;
  quantityLabel?: string;
}

const SCAN_MODES: ScanModeOption[] = [
  {
    label: "Receive Shipment (+1)",
    value: "SHIPMENT_RECEIVE",
    usesQuantity: false,
  },
  { label: "Pick for Order (-1)", value: "ORDER_PICK", usesQuantity: false },
  {
    label: "Inventory Count (record observed qty)",
    value: "INVENTORY_COUNT",
    usesQuantity: true,
    quantityLabel: "Counted quantity",
  },
  {
    label: "Cycle Count (record observed qty)",
    value: "CYCLE_COUNT",
    usesQuantity: true,
    quantityLabel: "Counted quantity",
  },
  {
    label: "Adjustment (+/- qty)",
    value: "ADJUSTMENT",
    usesQuantity: true,
    quantityLabel: "Adjustment (e.g. -2 or 5)",
  },
  {
    label: "Customer Return (+qty)",
    value: "RETURN_RECEIVE",
    usesQuantity: true,
    quantityLabel: "Returned quantity",
  },
];

interface RecentScan {
  barcode: string;
  partName: string;
  mode: ScanMode;
  notes: string | null;
  newCount: number;
  at: Date;
}

const isCountMode = (mode: ScanMode): boolean =>
  mode === "INVENTORY_COUNT" || mode === "CYCLE_COUNT";

function InventoryScan(): JSX.Element {
  const [barcode, setBarcode] = useState("");
  const [mode, setMode] = useState<ScanModeOption>(SCAN_MODES[0]);
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scannedPart, setScannedPart] = useState<Part | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);

  const handleScan = async () => {
    const trimmed = barcode.trim();
    if (!trimmed || submitting) return;
    if (mode.usesQuantity && quantity.trim() === "") {
      setError(`${mode.quantityLabel || "Quantity"} is required for this mode`);
      return;
    }

    setSubmitting(true);
    setError(null);
    setResult(null);
    setScannedPart(null);

    try {
      const scanResult = await submitScan({
        barcode: trimmed,
        scanMode: mode.value,
        quantity: mode.usesQuantity ? Number(quantity) : undefined,
      });

      // Enrich the result with part details for display
      let part: Part | null = null;
      try {
        part = await getPartByBarcode(trimmed);
      } catch {
        part = null;
      }

      setResult(scanResult);
      setScannedPart(part);
      setRecentScans((previous) =>
        [
          {
            barcode: trimmed,
            partName: part?.name || `Part #${scanResult.scanLogEntry.partId}`,
            mode: mode.value,
            notes: scanResult.scanLogEntry.notes,
            newCount: scanResult.scanLogEntry.newCount,
            at: new Date(),
          },
          ...previous,
        ].slice(0, 10)
      );
      setBarcode("");
      setQuantity("");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Scan failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inventory = result?.updatedInventory;
  const log = result?.scanLogEntry;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Scan Part Barcode
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <Autocomplete
                      options={SCAN_MODES}
                      value={mode}
                      disableClearable
                      onChange={(_, newMode) => {
                        if (newMode) setMode(newMode);
                      }}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => (
                        <MDInput {...params} label="Scan Mode" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={mode.usesQuantity ? 5 : 8}>
                    <MDInput
                      label="Scan or enter part / OEM number"
                      fullWidth
                      value={barcode}
                      disabled={submitting}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setBarcode(e.target.value)
                      }
                      onKeyPress={(e: React.KeyboardEvent) =>
                        e.key === "Enter" && handleScan()
                      }
                    />
                  </Grid>
                  {mode.usesQuantity && (
                    <Grid item xs={12} md={3}>
                      <MDInput
                        label={mode.quantityLabel || "Quantity"}
                        type="number"
                        fullWidth
                        value={quantity}
                        disabled={submitting}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setQuantity(e.target.value)
                        }
                        onKeyPress={(e: React.KeyboardEvent) =>
                          e.key === "Enter" && handleScan()
                        }
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={submitting || !barcode.trim()}
                      onClick={handleScan}
                    >
                      {submitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <>
                          <Icon>qr_code_scanner</Icon>&nbsp;Scan
                        </>
                      )}
                    </MDButton>
                  </Grid>
                </Grid>

                {error && (
                  <MDBox mt={2}>
                    <Alert severity="error">{error}</Alert>
                  </MDBox>
                )}

                {inventory && log && (
                  <MDBox mt={4}>
                    <Card sx={{ bgcolor: "grey.100" }}>
                      <MDBox p={3}>
                        <MDTypography variant="h5" mb={2}>
                          {scannedPart?.name || `Part #${log.partId}`}
                        </MDTypography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <MDTypography variant="caption" color="text">
                              Part Number
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight="medium">
                              {scannedPart?.partNumber || "—"}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6}>
                            <MDTypography variant="caption" color="text">
                              Bin Location
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight="medium">
                              {inventory.binLocation || "—"}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6}>
                            <MDTypography variant="caption" color="text">
                              On Hand
                            </MDTypography>
                            <MDTypography variant="h4" color="success">
                              {inventory.quantityOnHand}
                            </MDTypography>
                          </Grid>
                          {isCountMode(log.scanMode) && (
                            <Grid item xs={6}>
                              <MDTypography variant="caption" color="text">
                                Recorded Count
                              </MDTypography>
                              <MDTypography variant="h4" color="warning">
                                {inventory.lastCountQty ?? "—"}
                              </MDTypography>
                            </Grid>
                          )}
                          {!isCountMode(log.scanMode) && (
                            <Grid item xs={6}>
                              <MDTypography variant="caption" color="text">
                                Change
                              </MDTypography>
                              <MDTypography variant="h4" color="info">
                                {log.previousCount} → {log.newCount}
                              </MDTypography>
                            </Grid>
                          )}
                        </Grid>
                        {log.notes && (
                          <MDBox mt={2}>
                            <Alert
                              severity={isCountMode(log.scanMode) ? "info" : "success"}
                            >
                              {log.notes}
                            </Alert>
                          </MDBox>
                        )}
                      </MDBox>
                    </Card>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>
                  Recent Scans
                </MDTypography>
                {recentScans.length === 0 ? (
                  <MDTypography variant="body2" color="text">
                    Scans you submit in this session will appear here.
                  </MDTypography>
                ) : (
                  recentScans.map((scan, index) => (
                    <MDBox
                      key={`${scan.barcode}-${scan.at.getTime()}-${index}`}
                      py={1}
                      borderBottom="1px solid #eee"
                    >
                      <MDTypography variant="body2" fontWeight="medium">
                        {scan.partName}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {scan.barcode} · {scan.mode} ·{" "}
                        {scan.at.toLocaleTimeString()}
                      </MDTypography>
                      {scan.notes && (
                        <MDTypography variant="caption" color="info" display="block">
                          {scan.notes}
                        </MDTypography>
                      )}
                    </MDBox>
                  ))
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InventoryScan;
